import LoopError from '../store/errors/LoopError'
import { BaseApiClient } from './BaseApiClient'
import { CategoryJSON, PaginationInfo, Category } from '@scrubtech/models'

export interface CategoriesResponse {
  categories: Category[]
  paginationInfo: PaginationInfo
  requestParams: CategoriesRequestParams
}

export interface CategoriesErrorResponse {
  error: Error
  requestParams: CategoriesRequestParams
}

export interface CategoriesRequestParams {
  page: number
  limit: number
  search: string
}

export interface CategoryByIDRequestParams {
  categoryID: number
}

export interface CategoryByIDErrorResponse {
  error: Error
  requestParams: CategoryByIDRequestParams
}

export interface PostCategoryRequestParams {
  name: string
}

export interface PostCategoryErrorResponse {
  error: Error
  requestParams: PostCategoryRequestParams
}

export interface PutCategoryRequestParams extends PostCategoryRequestParams {
  categoryID: number
}

export interface PutCategoryErrorResponse {
  error: Error
  requestParams: PutCategoryRequestParams
}

class CategoriesApiClient extends BaseApiClient {
  constructor() {
    super('categories');
  }

  async getCategories(page: number = 1, limit: number = 30, search: string = ''): Promise<{ items: Category[], paginationInfo: PaginationInfo }> {
    try {
      return await this.getList<Category>(
        (json: CategoryJSON) => new Category(json),
        { page, limit, search }
      );
    } catch (err) {
      throw new LoopError(err, { page, limit, search });
    }
  }

  async getCategoryByID(categoryID: number): Promise<Category> {
    try {
      return await this.getByID<Category>(
        categoryID,
        (json: CategoryJSON) => new Category(json)
      );
    } catch (err) {
      throw new LoopError(err, { categoryID });
    }
  }

  async putCategory(categoryID: number, name: string): Promise<Category> {
    try {
      return await this.update<Category, { name: string }>(
        categoryID,
        { name },
        (json: CategoryJSON) => new Category(json)
      );
    } catch (err) {
      if (err instanceof LoopError) {
        throw new LoopError(err.error, { ...err.requestParams, categoryID, name });
      }
      throw new LoopError(err, { categoryID, name });
    }
  }

  async deleteCategory(categoryID: number): Promise<Category> {
    try {
      return await this.delete<Category>(
        categoryID,
        (json: CategoryJSON) => new Category(json)
      );
    } catch (err) {
      if (err instanceof LoopError) {
        throw new LoopError(err.error, { ...err.requestParams, categoryID });
      }
      throw new LoopError(err, { categoryID });
    }
  }

  async postCategory(name: string): Promise<Category> {
    try {
      return await this.create<Category, { name: string }>(
        { name },
        (json: CategoryJSON) => new Category(json)
      );
    } catch (err) {
      if (err instanceof LoopError) {
        throw new LoopError(err.error, { ...err.requestParams, name });
      }
      throw new LoopError(err, { name });
    }
  }
}

// Create a singleton instance
const categoriesApi = new CategoriesApiClient();

// Export the API functions with the same interface as before
export const getCategories = (page?: number, limit?: number, search?: string) => 
  categoriesApi.getCategories(page, limit, search);

export const getCategoryByID = (categoryID: number) => 
  categoriesApi.getCategoryByID(categoryID);

export const putCategory = (categoryID: number, name: string) => 
  categoriesApi.putCategory(categoryID, name);

export const deleteCategory = (categoryID: number) => 
  categoriesApi.deleteCategory(categoryID);

export const postCategory = (name: string) => 
  categoriesApi.postCategory(name);
